import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Upgrade from '@mui/icons-material/Upgrade';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box, { BoxProps } from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';
import {
  AccountType,
  CompanyShareableType,
  ModelError_Item,
  ModelError_Response,
} from '@treadinc/horizon-api-spec';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Account } from '~hooks/useAccount/models';
import useAccount from '~hooks/useAccount/useAccount';
import { CompanyBasic } from '~hooks/useCompany/models';
import { useCompanyShares } from '~hooks/useCompanyShares/useCompanyShares';
import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import { DriverBasic } from '~hooks/useDrivers/models';
import { BasicEquipment } from '~hooks/useEquipment/models';
import { ADMIN_ACTIONS_COLUMN_FIELD } from '~pages/Admin/adminActionsColumnField';
import { AccountFormStateChangeProps } from '~pages/Settings/Accounts/AccountFormStateChangeProps';
import { ConnectedAccountForm as AccountForm } from '~pages/Settings/Accounts/ConnectedAccountForm';
import { useCompanySharesDetails } from '~pages/Settings/Accounts/useCompanyShareDetails';
import useCursorPaginationQuery from '~queries/useCursorPaginationQuery';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { dateFormat } from '~utils/dateTime';

import PromoteAccountDialog from './PromoteAccountDialog';

const SelectorConnectionCTAWrapper = styled(Box)<BoxProps>(() => ({
  display: 'flex' as const,
  gap: '16px',
  alignItems: 'center',
  width: '540px',
  marginBottom: '24px',
}));

const AdminAccountsDataGrid = () => {
  const {
    control,
    formState: { errors },
    watch,
  } = useForm();
  const { getAccountsByCompanyIdPaginated, deleteAccount, updateAccount } = useAccount();
  const theme = useTheme();
  const { toasterStore } = useStores();
  const selectedCompany: Nullable<CompanyBasic> = watch('connections-companies-selector');
  const [isAccountLoading, setIsAccountLoading] = useState(false);
  const { searchValue, setSearchQueryValue } = useDataGridSearch();
  const {
    goToPage: goToAccountsPage,
    isFetching: isFetchingAccounts,
    pagination,
    reloadCurrentPage: reloadAccountsPage,
    rows,
  } = useCursorPaginationQuery<Account>({
    fetchPage: ({ cursor, limit, link, signal }) =>
      selectedCompany
        ? getAccountsByCompanyIdPaginated({
            companyId: selectedCompany.id,
            cursor,
            limit,
            link,
            searchQuery: searchValue,
            signal,
          })
        : Promise.resolve({ data: [], pagination: { limit, page: 1 } }),
    limit: MAX_API_PAGE_SIZE,
    queryKey: ['admin-accounts', selectedCompany?.id ?? null, searchValue],
  });
  const isLoading = isAccountLoading || isFetchingAccounts;
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [isDeleteAccountDialogOpen, setIsDeleteAccountDialogOpen] = useState(false);
  const [deleteAccountDialogError, setDeleteAccountDialogError] = useState<{
    code: number | null;
    message: string | null;
  }>({ code: null, message: null });
  const [isAccountDialogOpen, setIsAccountDialogOpen] = useState(false);
  const [isPromoteDialogOpen, setIsPromoteDialogOpen] = useState(false);
  const [currentFormDirty, setCurrentFormDirty] = useState<boolean>(false);
  const accountActionsRef = useRef<any>(null);
  const { createCompanyShareByCompanyId, deleteCompanyShare } = useCompanyShares();
  const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);

  const selectedAccount = rows.find((row) => row.id === selectedAccountId) || undefined;

  const {
    drivers,
    equipment,
    sharedDriversCompanyShares,
    sharedEquipmentCompanyShares,
    setEquipment,
    setDrivers,
    setSharedDriversCompanyShares,
    setSharedEquipmentCompanyShares,
  } = useCompanySharesDetails({
    isAccountDialogOpen,
    selectedAccountId,
    selectedAccount,
  });

  const onFormStateChange = ({ isDirty }: AccountFormStateChangeProps) => {
    setCurrentFormDirty(isDirty);
  };

  const closeDeleteAccountDialog = () => {
    setDeleteAccountDialogError({ code: null, message: null });
    setIsDeleteAccountDialogOpen(false);
  };

  const deleteRow = (id: string) => {
    setSelectedAccountId(id);
    setDeleteAccountDialogError({ code: null, message: null });
    setIsDeleteAccountDialogOpen(true);
  };

  const editRow = (id: string) => {
    setSelectedAccountId(id);
    setIsAccountDialogOpen(true);
  };

  const promoteRow = (id: string) => {
    setSelectedAccountId(id);
    setIsPromoteDialogOpen(true);
  };

  const closePromoteDialog = () => {
    setIsPromoteDialogOpen(false);
    setSelectedAccountId(null);
  };

  const onPromoteSuccess = async () => {
    await reloadAccountsPage();
  };

  const deleteAccountCallBack = async () => {
    if (!selectedAccountId) {
      return;
    }

    try {
      setIsAccountLoading(true);
      await deleteAccount({
        companyId:
          selectedCompany?.id ||
          rows.find((account) => account.id === selectedAccountId)?.company?.id,
        id: selectedAccountId,
      });
    } catch (error) {
      const typedError = error as AxiosError;

      setDeleteAccountDialogError({
        code: typedError.response?.status || null,
        message: typedError.message || 'An unexpected error occurred',
      });
      return;
    } finally {
      setIsAccountLoading(false);
    }

    closeDeleteAccountDialog();
    toasterStore.push(alert(t('account.account_removed'), AlertTypes.success));

    await reloadAccountsPage();
  };

  const handleClose = () => {
    setIsAccountDialogOpen(false);
    setEquipment([]);
    setDrivers([]);
    setSharedDriversCompanyShares([]);
    setSharedEquipmentCompanyShares([]);
  };

  const onSuccess = async (account?: Account) => {
    setSelectedAccountId(null);

    handleClose();
    toasterStore.push(
      alert(
        selectedAccountId
          ? t('account.account_updated', { name: account?.name })
          : t('account.account_created', { name: account?.name }),
        AlertTypes.success,
      ),
    );
    await reloadAccountsPage();
  };

  const onSubmitAccountFormCallBack = async (data: Account | any) => {
    setIsAccountLoading(true);
    try {
      if (selectedAccountId) {
        const driversPresentOnModalOpen = drivers;
        const driversPresentOnSubmit = data.drivers;

        const equipmentPresentOnModalOpen = equipment;
        const equipmentPresentOnSubmit = data.equipment;

        const updatedAccount = await updateAccount({
          account: data,
          companyId: selectedCompany?.id,
        });

        const driversToCreate = driversPresentOnSubmit?.filter(
          (submittedDriver: { id: string }) =>
            !driversPresentOnModalOpen.some(
              (existingDriver) => existingDriver.id === submittedDriver.id,
            ),
        );

        const driversToDelete = driversPresentOnModalOpen.filter(
          (existingDriver) =>
            driversPresentOnSubmit &&
            !driversPresentOnSubmit.some(
              (submittedDriver: { id: string }) =>
                existingDriver.id === submittedDriver.id,
            ),
        );

        // Get the company shares for the drivers that we need to remove
        const driverCompanySharesToDelete = sharedDriversCompanyShares.filter(
          (companyShare) =>
            driversToDelete.some(
              (driver) => driver.id === companyShare.companyShareable.id,
            ),
        );

        const equipmentToCreate = equipmentPresentOnSubmit?.filter(
          (submittedEquipment: { id: string }) =>
            !equipmentPresentOnModalOpen.some(
              (existingEquipment) => existingEquipment.id === submittedEquipment.id,
            ),
        );

        const equipmentToDelete = equipmentPresentOnModalOpen.filter(
          (existingEquipment) =>
            equipmentPresentOnSubmit &&
            !equipmentPresentOnSubmit.some(
              (submittedEquipment: { id: string }) =>
                existingEquipment.id === submittedEquipment.id,
            ),
        );

        // Get the company shares for the equipment that we need to remove
        const equipmentCompanySharesToDelete = sharedEquipmentCompanyShares.filter(
          (companyShare) =>
            equipmentToDelete.some(
              (equip) => equip.id === companyShare.companyShareable.id,
            ),
        );

        const companySharePromises: Promise<void>[] = [];
        if (updatedAccount) {
          if (
            driversToCreate?.length &&
            updatedAccount?.company?.id &&
            updatedAccount?.connectedCompany?.id
          ) {
            driversToCreate.forEach((driver: DriverBasic) => {
              companySharePromises.push(
                createCompanyShareByCompanyId({
                  companyId: updatedAccount.company?.id || '',
                  receiver_company_id: updatedAccount.connectedCompany?.id || '',
                  company_shareable_id: driver.id,
                  company_shareable_type: 'User' as CompanyShareableType,
                }),
              );
            });
          }

          if (driverCompanySharesToDelete?.length) {
            driverCompanySharesToDelete.forEach((driverCompanyShare) => {
              companySharePromises.push(
                deleteCompanyShare(driverCompanyShare.id as string),
              );
            });
          }

          if (
            equipmentToCreate?.length &&
            updatedAccount?.company?.id &&
            updatedAccount?.connectedCompany?.id
          ) {
            equipmentToCreate.forEach((equip: BasicEquipment) => {
              companySharePromises.push(
                createCompanyShareByCompanyId({
                  companyId: updatedAccount.company?.id || '',
                  receiver_company_id: updatedAccount.connectedCompany?.id || '',
                  company_shareable_id: equip.id,
                  company_shareable_type: 'Equipment' as CompanyShareableType,
                }),
              );
            });
          }

          if (equipmentCompanySharesToDelete?.length) {
            equipmentCompanySharesToDelete.forEach((equipCompanyShare) => {
              companySharePromises.push(
                deleteCompanyShare(equipCompanyShare.id as string),
              );
            });
          }
        }

        await Promise.all(companySharePromises);
        await onSuccess(data);
      }
    } catch (error) {
      const axiosError = error as AxiosError<ModelError_Response>;
      setAsyncErrors(axiosError.response?.data.error.errors || []);
    } finally {
      setIsAccountLoading(false);
    }
  };

  const onSubmitAccountForm = () => {
    accountActionsRef.current?.submit(onSubmitAccountFormCallBack);
  };

  const accountsColumns = useMemo(() => {
    const columns: Array<DataTableColumn<Account>> = [
      {
        field: 'name',
        headerName: t('form_fields.account_name'),
        valueGetter: (account) => account.name,
      },
      {
        field: 'company',
        headerName: t('form_fields.company'),
        valueGetter: (account) => account.company?.legalName,
      },
      {
        field: 'company.connected',
        headerName: t('form_fields.connected'),
        renderCell: (account) => {
          const { connectedCompany, id } = account;
          const isConnectedLabel = connectedCompany ? 'Yes' : 'No';

          return (
            <Tooltip title={isConnectedLabel} key={`tooltip-account-type-key-${id}`}>
              <Chip
                key={id}
                label={isConnectedLabel}
                color={connectedCompany ? 'success' : 'info'}
                variant={'outlined'}
              />
            </Tooltip>
          );
        },
      },
      {
        field: 'accountTypes',
        headerName: t('form_fields.type'),
        renderCell: (account) => (
          <>
            {account.accountTypes
              .slice()
              .sort()
              .map((type, index) => (
                <Tooltip title={type} key={`tooltip-account-type-key-${type}`}>
                  <Chip
                    sx={{ ml: index !== 0 ? 1 : 0 }}
                    key={type}
                    label={type}
                    color={type === AccountType.VENDOR ? 'info' : 'success'}
                    variant={'outlined'}
                  />
                </Tooltip>
              ))}
          </>
        ),
      },
      {
        field: 'address.fullAddress',
        headerName: t('form_fields.address'),
        valueGetter: (account) => {
          if (!account.address) return '';
          const { streetAddress, premise, country, administrativeArea } = account.address;

          return `${streetAddress} ${premise}, ${administrativeArea}, ${country?.name ?? ''}`;
        },
      },
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        renderCell: (account) => (
          <Tooltip title={`${t('common.last_update')}: ${dateFormat(account.updatedAt)}`}>
            <Typography component="span">{dateFormat(account.createdAt)}</Typography>
          </Tooltip>
        ),
      },
      actionsColumn((account) => {
        const menuOptions = [
          {
            title: t('actions.delete'),
            icon: <Delete />,
            callBack: () => deleteRow(account.id),
          },
        ];

        // Promotion only applies to flat accounts (no connected company).
        if (!account.connectedCompany) {
          menuOptions.unshift({
            title: t('promote_account.action_label'),
            icon: <Upgrade />,
            callBack: () => promoteRow(account.id),
          });
        }

        return (
          <>
            <IconButton
              aria-label={t('actions.edit')}
              size="small"
              onClick={() => editRow(account.id)}
            >
              <Edit />
            </IconButton>
            <SimpleMenu options={menuOptions}>
              <MoreHoriz />
            </SimpleMenu>
          </>
        );
      }, ADMIN_ACTIONS_COLUMN_FIELD),
    ];

    return columns;
  }, []);

  const onChangeQuickFilter = (searchQuery: string) => {
    setSearchQueryValue(searchQuery);
  };

  return (
    <Box>
      <SelectorConnectionCTAWrapper>
        <CompanySelectorFormField
          name="connections-companies-selector"
          errors={errors}
          control={control}
          hideLabel={true}
          inputProps={{
            placeholder: `${t('form_fields.company_selector_hint')}`,
          }}
          sx={{
            flexGrow: 1,
            flexShrink: 0,
          }}
          clearable
        />
      </SelectorConnectionCTAWrapper>

      <DataTable
        id="admin-accounts-grid"
        columns={accountsColumns}
        rows={rows}
        loading={isLoading}
        onSearchChange={onChangeQuickFilter}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isLoading}
            onNavigate={(link) => void goToAccountsPage(link)}
            pagination={pagination}
          />
        }
      />

      <Dialog
        isOpen={isAccountDialogOpen}
        onClose={handleClose}
        preventCloseOnBackdropClick
        maxWidth="lg"
      >
        <DialogHeader onClose={handleClose}>
          {selectedAccountId ? t('account.update_account') : t('account.create_account')}
        </DialogHeader>

        <DialogContent sx={{ backgroundColor: theme.semantic.surfaceSunken }}>
          <AccountForm
            ref={accountActionsRef}
            onFormStateChange={onFormStateChange}
            defaultAccount={selectedAccount}
            sharedDrivers={drivers}
            sharedEquipment={equipment}
            sharedDriversCompanyShares={sharedDriversCompanyShares}
            sharedEquipmentCompanyShares={sharedEquipmentCompanyShares}
            errors={asyncErrors}
          />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isLoading}
            onClick={handleClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="primary"
            data-test-id={'create-account-btn'}
            disabled={isLoading || !currentFormDirty}
            loading={isLoading}
            onClick={onSubmitAccountForm}
            variant="contained"
          >
            {selectedAccountId ? t('actions.submit') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        confirmButtonProps={{ children: `${t('actions.delete')}` }}
        isBusy={isLoading}
        isOpen={isDeleteAccountDialogOpen}
        onClose={closeDeleteAccountDialog}
        onConfirm={deleteAccountCallBack}
        title={t('account.delete_account_title')}
      >
        <Typography>{t('account.delete_account_text')}</Typography>

        <DeleteAccountErrorComponent code={deleteAccountDialogError.code} />
      </ConfirmationDialog>

      <PromoteAccountDialog
        account={selectedAccount ?? null}
        companyId={selectedCompany?.id ?? null}
        open={isPromoteDialogOpen}
        onClose={closePromoteDialog}
        onSuccess={() => {
          void onPromoteSuccess();
        }}
      />
    </Box>
  );
};

interface DeleteAccountErrorComponentProps {
  code: number | null;
}

function DeleteAccountErrorComponent({ code }: DeleteAccountErrorComponentProps) {
  if (code === 422) {
    return (
      <Alert severity="warning" sx={{ mt: 4 }}>
        <AlertTitle sx={{ fontSize: '16px', fontWeight: 600 }}>
          {t('account.delete_error_title')}
        </AlertTitle>

        <Typography variant="body1">{t('account.delete_error_text')}</Typography>
      </Alert>
    );
  }

  return null;
}

export default observer(AdminAccountsDataGrid);
